import React, { useCallback, useEffect, useState } from 'react'
import { Form, withFormik, Field } from 'formik'
import { requestDashboardAccess, updateCustomer } from '../../../_common/core/http_services'
import { withRouter } from 'react-router'
import _get from 'lodash/get'
import _includes from 'lodash/includes'
import Button from '../../_library/Button'
import { combineValidators, isEmailValidator, requiredValidator } from '../../../_common/core/validation'
import { RadioGroupField, SimpleField } from '../../formik/Fields'
import { movedToCreateAccount, movedToSignIn } from '../../../_common/core/gtag'
import { deleteCookieByName } from '../../utils/cookies'
import { ENV } from '../../constants/env'
import { googleOauth } from '../../../_common/core/http_services/oauth'
import { showAxiosError } from '../../utils/messenger'

const EmailForm = props => {
  const { isSubmitting, isValid, values } = props

  const [isLoading, setIsLoading] = useState(false)

  const isBuyOrResellSelected = values.best_describe === '3'

  const handleGoBack = () => {
    props.history.goBack()
  }

  const handleGoogleSignIn = useCallback(
    async res => {
      if (!res.clientId || !res.credential) return
      try {
        setIsLoading(true)
        await googleOauth(res.credential)
        await updateCustomer({ best_describe: values.best_describe })
        isProd() && fbq('trackCustom', 'CustomerLoggedInGoogleOauthSuccesfully', { clientId: res.clientId })

        props.history.push('/welcome/create-brand', {
          createBrandOnly: true,
        })
      } catch (e) {
        setIsLoading(false)
      }
    },
    [props.history, values.best_describe],
  )

  useEffect(() => {
    if (isSubmitting) {
      return
    }

    const initializeGsi = () => {
      if (!window.google) return
      window.google.accounts.id.initialize({
        client_id: ENV.GOOGLE_OAUTH_CLIENT_ID,
        callback: handleGoogleSignIn,
      })
    }
    const runScript = () => {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.onload = initializeGsi
      script.async = true
      script.id = 'google-client-script'
      document.querySelector('body')?.appendChild(script)
    }
    runScript()
    setTimeout(() => {
      runScript()
    }, 0)
    return () => {
      window.google?.accounts.id.cancel()
      document.getElementById('google-client-script')?.remove()
    }
  }, [handleGoogleSignIn, isSubmitting])

  return (
    <Form className="margin-horizontal-25 sign-up-container">
      <div className="field-wrapper">
        <label className="label-best-describe">What best describes you?</label>
        <Field
          type="best_describe"
          name={'best_describe'}
          component={RadioGroupField}
          validate={requiredValidator()}
          groups={[
            {
              label: 'I want to Buy or Resell Tickets',
              value: '3',
            },
            {
              label: 'Event Organizer/Manager/Marketing',
              value: '1',
            },
            {
              label: 'Venue Owner/Manager/Marketing',
              value: '2',
            },
          ]}
          classNames={{
            container: 'radio-group-container single-column',
            container_item: 'radio-group-item',
            component: '',
          }}
        />
        {isBuyOrResellSelected ? (
          <div className="error-container">
            <p>
              This sign-up form is only for event orgqnizers or venue owners. Please go to
              <a href="http://www.ticketfairy.com" target="_blank" rel="noopener noreferrer">
                {' '}
                www.ticketfairy.com{' '}
              </a>
              to buy or resell tickets.
            </p>
          </div>
        ) : null}
      </div>

      {!values.best_describe || values.best_describe === '3' ? null : (
        <>
          <Field
            type="email"
            name="email"
            label="Email"
            component={SimpleField}
            formclassname="field-wrapper margin-top-24"
            touchedOnBlur
            validate={combineValidators(requiredValidator(), isEmailValidator('Invalid'))}
          />

          <div
            className="buttons-wrapper single-button"
            style={{ justifyContent: 'center', marginTop: '12px', marginBottom: '24px' }}
          >
            <Button
              type="submit"
              className={!isValid ? 'submit-disabled' : 'submit-button-login'}
              disabled={!isValid}
              loading={isSubmitting}
            >
              Continue with Email
            </Button>
          </div>

          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#666' }}>- or sign up with Google -</p>
          </div>

          {/* This div creates a placeholder for proper spacing */}
          <div style={{ height: '30px' }} />
        </>
      )}

      {/* Google button at the root level for proper initialization */}
      {isSubmitting ? null : isLoading ? (
        <button
          type="button"
          className={'back-button'}
          disabled
          style={{
            display: !values.best_describe || values.best_describe === '3' ? 'none' : 'block',
            margin: '0 auto',
          }}
        >
          <i className="fa fa-circle-o-notch fa-fw fa-spin" />
        </button>
      ) : (
        <Button
          type="button"
          className={'g_id_signin'}
          disabled={!values.best_describe || values.best_describe === '3'}
          style={{
            padding: '13px 0px',
            display: !values.best_describe || values.best_describe === '3' ? 'none' : 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto',
            marginTop: -30,
            border: 'none',
            background: 'transparent',
          }}
        />
      )}

      <div className="buttons-wrapper">
        <Button type="button" className="back-button" disabled={isSubmitting} onClick={handleGoBack}>
          Back
        </Button>
      </div>
    </Form>
  )
}

const EmailFormFormik = withFormik({
  mapPropsToValues: props => {
    const searchParams = new URLSearchParams(window.location.search)
    const queryEmail = searchParams.get('email') || ''
    const stateEmail = props?.location?.state?.email || ''
    const prefillEmail = stateEmail || queryEmail
    return { email: prefillEmail, best_describe: props?.location?.state?.best_describe || '' }
  },
  validate: values => {
    const errors = {}
    const { email, best_describe } = values

    if (!email) {
      errors.email = 'Required'
    } else if (!validateEmail(email)) {
      errors.email = 'Invalid email'
    }

    if (!best_describe) {
      errors.best_describe = 'Required'
    }

    return errors
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    const { email, best_describe } = values
    try {
      await requestDashboardAccess(email)
      deleteCookieByName('X-TF-ECOMMERCE')
      props.history.push('/welcome/sign-in', { email })
      isProd() && fbq('trackCustom', 'CustomerHasAccount', { email })
      movedToSignIn()
    } catch (e) {
      const errorMessage = _get(e, 'response.data.message')
      if (_includes(errorMessage, 'twitch')) {
        showAxiosError(e)
        setSubmitting(false)
      } else {
        const params = new URL(window.location.href).searchParams
        const referral = params.get('r')
        props.history.push('/welcome/create-account', { email, referral, best_describe })
        movedToCreateAccount()
        isProd() && fbq('trackCustom', 'CustomerHasNoAccount', { email })
        setSubmitting(false)
      }
    }
  },
  displayName: 'EmailForm',
})(EmailForm)

const EmailFormRouter = withRouter(EmailFormFormik)

export default EmailFormRouter
